import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Video, CheckCircle, XCircle, AlertCircle, Loader2, X, ExternalLink, Filter, Mail, Phone } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { consultationService, Consultation } from '../services/consultationService';
import Pagination from '../components/Pagination';
import HeroSection from '../components/HeroSection';
import heroData from '../data/heroData.json';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

type FilterType = 'ALL' | 'UPCOMING' | 'PAST' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';

const MyConsultations = () => {
  const { } = useAuth();
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterType>('ALL');
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(showModal);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const statusConfig = {
    PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
    SCHEDULED: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Calendar },
    COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filter changes
  }, [filter]);

  useEffect(() => {
    fetchConsultations();
  }, [currentPage, filter]);

  const fetchConsultations = async () => {
    setLoading(true);
    setError('');
    try {
      // Map filter to API status parameter
      let status: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | undefined;
      
      if (filter === 'SCHEDULED' || filter === 'COMPLETED' || filter === 'CANCELLED') {
        status = filter;
      } else if (filter === 'ALL') {
        status = undefined; // Fetch all
      } else {
        // For UPCOMING and PAST, we'll fetch all and filter client-side for now
        // as these require date-based logic
        status = undefined;
      }

      const response = await consultationService.getMyConsultations(currentPage, 9, status);
      
      // Apply date-based filters client-side for UPCOMING and PAST
      let filteredConsultations = response.consultations;
      if (filter === 'UPCOMING') {
        const now = new Date();
        filteredConsultations = response.consultations.filter(consultation => {
          if (!consultation.scheduledDateTime) return false;
          return new Date(consultation.scheduledDateTime) > now && 
                 consultation.status !== 'CANCELLED' && 
                 consultation.status !== 'COMPLETED';
        });
      } else if (filter === 'PAST') {
        const now = new Date();
        filteredConsultations = response.consultations.filter(consultation => {
          if (!consultation.scheduledDateTime) return false;
          return new Date(consultation.scheduledDateTime) < now || 
                 consultation.status === 'COMPLETED';
        });
      }

      setConsultations(filteredConsultations);
      // For UPCOMING and PAST filters, we need to recalculate total pages based on filtered results
      // For other filters, use the API's totalPages
      if (filter === 'UPCOMING' || filter === 'PAST') {
        // We need to fetch all consultations to get accurate count for these filters
        // For now, use the filtered count but this might need backend support
        setTotalPages(Math.ceil(filteredConsultations.length / 9) || 1);
        setTotalCount(filteredConsultations.length);
      } else {
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount || filteredConsultations.length);
      }
    } catch (err: any) {
      console.error('Error fetching consultations:', err);
      setError(err.message || 'Failed to load consultations. Please try again.');
      setConsultations([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  const formatTime = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  const getConsultationTypeName = (consultation: Consultation): string => {
    if (typeof consultation.consultationTypeId === 'object' && consultation.consultationTypeId !== null) {
      return consultation.consultationTypeId.name || 'Consultation';
    }
    return 'Consultation';
  };

  const getConsultationTypeDuration = (consultation: Consultation): string => {
    if (typeof consultation.consultationTypeId === 'object' && consultation.consultationTypeId !== null) {
      return consultation.consultationTypeId.duration || '';
    }
    return '';
  };

  // Use consultations directly from API (already paginated)
  // Only apply additional client-side slicing for UPCOMING/PAST filters if needed
  const displayConsultations = consultations;

  return (
    <>
      <SEO
        title="My Consultations - BookMyYogaTTC"
        description="View and manage your yoga teacher training consultations"
        keywords="consultations, yoga consultation, my consultations"
      />
      
      <div className="pt-20">
      <HeroSection
        title={heroData.myConsultations.title}
        description={heroData.myConsultations.description}
        patternId="myConsultationsPattern"
      />
      </div>
      <div className="bg-fog pb-16 pt-8">
        <div className="container-custom">
          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 flex flex-wrap gap-3"
          >
            <button
              onClick={() => setFilter('ALL')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filter === 'ALL'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              <Filter size={16} />
              All
            </button>
            <button
              onClick={() => setFilter('UPCOMING')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'UPCOMING'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilter('SCHEDULED')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'SCHEDULED'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              Scheduled
            </button>
            <button
              onClick={() => setFilter('PAST')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'PAST'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              Past
            </button>
            <button
              onClick={() => setFilter('COMPLETED')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'COMPLETED'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilter('CANCELLED')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'CANCELLED'
                  ? 'bg-primary text-white'
                  : 'bg-white text-ivory-700 border border-ivory-300 hover:bg-ivory-100'
              }`}
            >
              Cancelled
            </button>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin h-12 w-12 text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Consultations Grid */}
          {!loading && !error && (
            <>
              {consultations.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 bg-white rounded-lg shadow-sm"
                >
                  <Calendar className="mx-auto h-16 w-16 text-ivory-400 mb-4" />
                  <h3 className="text-5 font-semibold text-ivory-900 mb-2">No consultations found</h3>
                  <p className="text-ivory-600">
                    {filter === 'ALL'
                      ? "You haven't booked any consultations yet."
                      : `No ${filter.toLowerCase()} consultations found.`}
                  </p>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayConsultations.map((consultation) => {
                    const StatusIcon = statusConfig[consultation.status]?.icon || AlertCircle;
                    const statusInfo = statusConfig[consultation.status] || statusConfig.PENDING;
                    const isUpcoming = consultation.scheduledDateTime && new Date(consultation.scheduledDateTime) > new Date();

                    return (
                      <motion.div
                        key={consultation._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -4 }}
                        className="bg-white rounded-lg shadow-sm border border-ivory-200 p-6 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => {
                          setSelectedConsultation(consultation);
                          setShowModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-4 font-semibold text-ivory-900 mb-1">
                              {getConsultationTypeName(consultation)}
                            </h3>
                            <p className="text-2 text-ivory-600">{getConsultationTypeDuration(consultation)}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full border text-1 font-medium flex items-center gap-1 ${statusInfo.color}`}>
                            <StatusIcon size={12} />
                            {statusInfo.label}
                          </div>
                        </div>

                        {consultation.scheduledDateTime && (
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-2 text-ivory-700">
                              <Calendar size={16} className="text-primary" />
                              <span>{formatDate(consultation.scheduledDateTime)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2 text-2 text-ivory-700">
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-primary" />
                                <span>{formatTime(consultation.scheduledDateTime)}</span>
                              </div>
                              {consultation.googleMeetLink && isUpcoming && (
                                <a
                                  href={consultation.googleMeetLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 text-2 text-blue-600 hover:text-blue-700 font-medium"
                                >
                                  <Video size={16} />
                                  Join Meet
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-ivory-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedConsultation(consultation);
                              setShowModal(true);
                            }}
                            className="text-2 text-primary hover:text-primary-dark font-medium"
                          >
                            View Details →
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {consultations.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={9}
                  currentItemsCount={displayConsultations.length}
                  onPageChange={setCurrentPage}
                  loading={loading}
                  showCount={true}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Consultation Details Modal */}
      <AnimatePresence>
        {showModal && selectedConsultation && (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              touchAction: 'none',
              overflow: 'hidden',
              overscrollBehavior: 'none'
            }}
            onTouchStart={(e) => {
              // Prevent all touch events on backdrop
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onTouchMove={(e) => {
              // Prevent backdrop scrolling
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onTouchEnd={(e) => {
              // Prevent touch end events
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowModal(false);
                setSelectedConsultation(null);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              style={{ 
                touchAction: 'pan-y',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch' as any
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-6 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-5 font-semibold">Consultation Details</h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedConsultation(null);
                    }}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-full border text-2 font-medium flex items-center gap-1 bg-white/20 border-white/30`}>
                    {(() => {
                      const StatusIcon = statusConfig[selectedConsultation.status]?.icon || AlertCircle;
                      const statusInfo = statusConfig[selectedConsultation.status] || statusConfig.PENDING;
                      return (
                        <>
                          <StatusIcon size={14} />
                          {statusInfo.label}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Consultation Type */}
                  <div>
                    <h3 className="text-4 font-semibold text-ivory-900 mb-2">Consultation Type</h3>
                    <p className="text-ivory-700">{getConsultationTypeName(selectedConsultation)}</p>
                    {getConsultationTypeDuration(selectedConsultation) && (
                      <p className="text-2 text-ivory-600 mt-1">{getConsultationTypeDuration(selectedConsultation)}</p>
                    )}
                  </div>

                  {/* Scheduled Date & Time */}
                  {selectedConsultation.scheduledDateTime && (
                    <div>
                      <h3 className="text-4 font-semibold text-ivory-900 mb-2">Scheduled Date & Time</h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-ivory-700">
                          <Calendar size={18} className="text-primary" />
                          <span>{formatDate(selectedConsultation.scheduledDateTime)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-ivory-700">
                          <Clock size={18} className="text-primary" />
                          <span>{formatTime(selectedConsultation.scheduledDateTime)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Google Meet Link */}
                  {selectedConsultation.googleMeetLink && (
                    <div>
                      <h3 className="text-4 font-semibold text-ivory-900 mb-2">Meeting Link</h3>
                      <a
                        href={selectedConsultation.googleMeetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-medium"
                      >
                        <Video size={18} />
                        Join Google Meet
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  )}

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-4 font-semibold text-ivory-900 mb-2">Contact Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-ivory-700">
                        <Mail size={18} className="text-primary" />
                        <span>{selectedConsultation.email}</span>
                      </div>
                      {selectedConsultation.phone && (
                        <div className="flex items-center gap-2 text-ivory-700">
                          <Phone size={18} className="text-primary" />
                          <span>{selectedConsultation.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Created Date */}
                  <div>
                    <h3 className="text-4 font-semibold text-ivory-900 mb-2">Booked On</h3>
                    <p className="text-ivory-700">
                      {new Date(selectedConsultation.createdAt).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t p-6 flex-shrink-0 flex justify-end">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedConsultation(null);
                  }}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MyConsultations;

